/**
 * Product visuals for primary SEO landing paths (public /images/seo/*.webp).
 * Images are WebP copies of existing in-app marketing screenshots (doc editor, signing, completion).
 */

/**
 * @typedef {{ src: string, alt: string, width: number, height: number, eager?: boolean, caption?: string }} SeoFigureSpec
 * `caption` — short figcaption (first image on each page; optional on one more where helpful). Not a duplicate of alt.
 */

/** @type {Record<string, SeoFigureSpec[]>} */
export const SEO_PRIMARY_PAGE_FIGURES = {
  '/': [
    {
      src: '/images/seo/home-prime-document-sign-editor.webp',
      width: 860,
      height: 1254,
      eager: true,
      alt: 'Prime Document Sign agreement editor with text, signature fields, and send workflow for online documents',
      caption: 'Send and sign documents online in minutes using Prime Document Sign.',
    },
  ],
  '/docusign-alternative': [
    {
      src: '/images/seo/docusign-alternative-recipient-signing-view.webp',
      width: 860,
      height: 1254,
      eager: true,
      alt: 'Recipient signing experience in Prime Document Sign in the browser without installing software',
      caption: 'Signers complete agreements in the browser—no installs or heavy enterprise tools required.',
    },
    {
      src: '/images/seo/docusign-alternative-prime-document-editor.webp',
      width: 860,
      height: 1254,
      alt: 'Prime Document Sign document workspace for drafting agreements and placing e-signature fields',
      caption: 'Prepare agreements and fields in a straightforward editor, then send one signing link.',
    },
  ],
  '/how-to-sign-documents-online': [
    {
      src: '/images/seo/how-to-sign-documents-online-step-compose.webp',
      width: 860,
      height: 1254,
      eager: true,
      alt: 'Step one: compose or upload a document and add signature and field blocks in Prime Document Sign',
      caption: 'Step-by-step workflow: start from your document, then add signature and field blocks.',
    },
    {
      src: '/images/seo/how-to-sign-documents-online-step-sign-fields.webp',
      width: 860,
      height: 1254,
      alt: 'Step two: signers complete fields and apply their e-signature in the Prime Document Sign viewer',
    },
    {
      src: '/images/seo/how-to-sign-documents-online-step-signed-pdf.webp',
      width: 834,
      height: 1276,
      alt: 'Step three: download a completed signed PDF after everyone has signed in Prime Document Sign',
      caption: 'When signing finishes, download a completed PDF you can store or share.',
    },
  ],
  '/free-contract-templates': [
    {
      src: '/images/seo/free-contract-templates-signed-agreement.webp',
      width: 834,
      height: 1276,
      eager: true,
      alt: 'Signed agreement output from Prime Document Sign suitable for NDAs, freelance agreements, leases, and service contracts',
      caption: 'Turn starter templates into signed agreements you can reuse for common deal types.',
    },
    {
      src: '/images/seo/free-contract-templates-editor-starter.webp',
      width: 860,
      height: 1254,
      alt: 'Prime Document Sign editor used to adapt starter contract templates before sending for signature',
    },
  ],
  '/esignature-for-freelancers': [
    {
      src: '/images/seo/esignature-for-freelancers-client-signing.webp',
      width: 860,
      height: 1254,
      eager: true,
      alt: 'Client signing a freelancer agreement quickly in the browser with Prime Document Sign',
      caption: 'Clients sign proposals and contracts quickly—ideal for solo operators and small gigs.',
    },
    {
      src: '/images/seo/esignature-for-freelancers-agreement-draft.webp',
      width: 860,
      height: 1254,
      alt: 'Freelancer preparing a proposal or contract for e-signature in Prime Document Sign',
    },
  ],
  '/secure-esignature': [
    {
      src: '/images/seo/secure-esignature-completion-audit-preview.webp',
      width: 834,
      height: 1276,
      eager: true,
      alt: 'Completion screen emphasizing signed PDF delivery and audit-friendly metadata in Prime Document Sign',
      caption: 'Secure signing with a clear completion path and records you can refer back to.',
    },
    {
      src: '/images/seo/secure-esignature-encrypted-signing-session.webp',
      width: 860,
      height: 1254,
      alt: 'Secure in-browser signing session in Prime Document Sign with field-level capture',
    },
  ],
  '/security': [
    {
      src: '/images/seo/security-audit-friendly-completion-record.webp',
      width: 834,
      height: 1276,
      eager: true,
      alt: 'Completion and audit context after signing, illustrating traceable e-signature events in Prime Document Sign',
      caption: 'Signing events and completion context help teams evidence what happened, when.',
    },
    {
      src: '/images/seo/security-document-workspace-encryption-context.webp',
      width: 860,
      height: 1254,
      alt: 'Prime Document Sign document workspace running over HTTPS with Google-backed infrastructure for storage',
    },
  ],
  '/esignature-legality': [
    {
      src: '/images/seo/esignature-legality-signed-record-metadata.webp',
      width: 834,
      height: 1276,
      eager: true,
      alt: 'Signed document completion view supporting evidence of agreement and timestamps in Prime Document Sign',
      caption: 'A clear record of completion supports everyday evidence of who agreed and when.',
    },
    {
      src: '/images/seo/esignature-legality-signing-evidence-workflow.webp',
      width: 860,
      height: 1254,
      alt: 'E-signature capture flow in Prime Document Sign showing signer actions tied to the agreement',
    },
  ],
  '/how-it-works': [
    {
      src: '/images/seo/how-it-works-step-create-send.webp',
      width: 860,
      height: 1254,
      eager: true,
      alt: 'Create or upload an agreement and send a signing link with Prime Document Sign',
      caption: 'Create your agreement, place fields, and send a link—simple flow from draft to signers.',
    },
    {
      src: '/images/seo/how-it-works-step-collect-signatures.webp',
      width: 860,
      height: 1254,
      alt: 'Collect signatures online as each party completes the document in Prime Document Sign',
    },
    {
      src: '/images/seo/how-it-works-step-download-completed.webp',
      width: 834,
      height: 1276,
      alt: 'Download the finished signed PDF from Prime Document Sign after completion',
      caption: 'Pick up the signed PDF as soon as the last signer finishes.',
    },
  ],
  '/pricing': [
    {
      src: '/images/seo/pricing-affordable-plans-overview.webp',
      width: 1200,
      height: 630,
      eager: true,
      alt: 'Prime Document Sign product overview graphic used alongside simple per-month and pay-per-send pricing',
      caption: 'Straightforward plans so you know what you are paying for before you commit.',
    },
  ],
  '/contact': [
    {
      src: '/images/seo/contact-support-response-signing.webp',
      width: 860,
      height: 1254,
      eager: true,
      alt: 'Signing experience in Prime Document Sign illustrating responsive support for senders and signers',
      caption: 'The same signing experience your recipients see—simple, guided, and browser-based.',
    },
  ],
  '/trust': [
    {
      src: '/images/seo/trust-platform-reliability-overview.webp',
      width: 1200,
      height: 630,
      eager: true,
      alt: 'Prime Document Sign marketing preview summarizing the product for trust and security pages',
      caption: 'Built for teams that want signing without sacrificing clarity on security and process.',
    },
    {
      src: '/images/seo/trust-completion-and-audit-trail.webp',
      width: 834,
      height: 1276,
      alt: 'Completed document view in Prime Document Sign highlighting traceable signing events',
    },
  ],
  '/about': [
    {
      src: '/images/seo/about-product-mission-editor.webp',
      width: 860,
      height: 1254,
      eager: true,
      alt: 'Prime Document Sign product editor reflecting the company focus on simple online agreements',
      caption: 'Product-led focus: fewer hoops between your agreement and a signed outcome.',
    },
  ],
  '/what-is-esignature': [
    {
      src: '/images/seo/what-is-esignature-simple-signing-flow.webp',
      width: 860,
      height: 1254,
      eager: true,
      alt: 'Signer view illustrating how an e-signature is applied to fields in Prime Document Sign',
      caption: 'An e-signature is applied in the browser on the fields you place—no printing required.',
    },
    {
      src: '/images/seo/what-is-esignature-agreement-setup.webp',
      width: 860,
      height: 1254,
      alt: 'Setting up an electronic signature workflow on an agreement in Prime Document Sign',
    },
  ],
}

/**
 * @param {string} logicalPath
 * @returns {SeoFigureSpec[] | null}
 */
export function getSeoFiguresForLandingPath(logicalPath) {
  const list = SEO_PRIMARY_PAGE_FIGURES[logicalPath]
  return list?.length ? list : null
}
